import { ValidationError } from "class-validator";
import Auth, { type DeliveryChannel, type IMailVerifyReturn } from "@Front/Api/Auth/Customer/Auth";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { LoginStep } from "../loginSteps";
import {
	buildCustomerLoginAuthScopePayload,
	type CustomerAuthDisambiguation,
} from "@Front/Components/Layouts/Login/helpers/loginFolderUidHelpers";
import { CUSTOMER_LOGIN_NEEDS_CHOICE_EMAIL_MESSAGE } from "@Front/Components/Layouts/Login/helpers/customerLoginNeedsChoiceMessages";
import { extractHttpError } from "./loginErrorHandlers";

type AskNewPasswordSetters = {
	setPartialPhoneNumber: (value: string) => void;
	setDeliveryChannel: (channel: DeliveryChannel) => void;
	setMaskedEmail: (value: string) => void;
	setTotpCodeUid: (uid: string) => void;
	setValidationErrors: (errors: ValidationError[]) => void;
	setStep: (step: LoginStep) => void;
};

function applyAskNewPasswordSuccessResponse(res: IMailVerifyReturn, setters: AskNewPasswordSetters): void {
	if (res.needsChoice) {
		setters.setValidationErrors([
			{
				property: "email",
				constraints: { "400": CUSTOMER_LOGIN_NEEDS_CHOICE_EMAIL_MESSAGE },
			},
		]);
		return;
	}
	if (!res.totpCodeUid || !res.deliveryChannel) {
		setters.setValidationErrors([
			{
				property: "email",
				constraints: { "400": "Impossible d’envoyer le code." },
			},
		]);
		return;
	}
	setters.setPartialPhoneNumber(res.partialPhoneNumber ?? "");
	setters.setDeliveryChannel(res.deliveryChannel);
	setters.setMaskedEmail(res.maskedEmail ?? "");
	setters.setTotpCodeUid(res.totpCodeUid);
	setters.setValidationErrors([]);
	setters.setStep(LoginStep.TOTP);
}

function handleAskNewPasswordRequestError(error: unknown, email: string, setters: AskNewPasswordSetters): void {
	const { http_status, message } = extractHttpError(error);
	LoggerService.error("askNewPassword failed", { error, email });
	if (http_status === 425) {
		setters.setStep(LoginStep.TOTP);
		return;
	}
	if (message) {
		setters.setValidationErrors([
			{
				property: "email",
				constraints: {
					[String(http_status ?? "unknown")]: message,
				},
			},
		]);
	}
}

export async function runAskNewPasswordUiFlow(input: {
	email: string;
	loginFolderUid: string | undefined;
	loginOfficeUid: string | undefined;
	loginCustomerUid: string | undefined;
	authDisambiguation: CustomerAuthDisambiguation;
} & AskNewPasswordSetters): Promise<void> {
	const { email, loginFolderUid, loginOfficeUid, loginCustomerUid, authDisambiguation, ...setters } = input;
	try {
		const res = await Auth.getInstance().askNewPassword({
			email,
			...buildCustomerLoginAuthScopePayload(loginFolderUid, loginOfficeUid, authDisambiguation, loginCustomerUid),
		});
		applyAskNewPasswordSuccessResponse(res, setters);
	} catch (error: unknown) {
		handleAskNewPasswordRequestError(error, email, setters);
	}
}
